import { useLanguage } from './i18n/LanguageContext';

function Pipeline() {
  const { t } = useLanguage();
  const projects = [
    {
      name: t.pipeline.dubai.name,
      location: t.pipeline.dubai.location,
      gfa: t.pipeline.dubai.gfa,
      target: t.pipeline.dubai.target,
      investment: t.pipeline.dubai.investment,
      image: '/dubai-aerial.jpg',
    },
    {
      name: t.pipeline.samutPrakan.name,
      location: t.pipeline.samutPrakan.location,
      gfa: t.pipeline.samutPrakan.gfa,
      target: t.pipeline.samutPrakan.target,
      investment: t.pipeline.samutPrakan.investment,
      image: '/warehouse1.jpg',
    },
    {
      name: t.pipeline.samutSakhon.name,
      location: t.pipeline.samutSakhon.location,
      gfa: t.pipeline.samutSakhon.gfa,
      target: t.pipeline.samutSakhon.target,
      investment: t.pipeline.samutSakhon.investment,
      image: '/warehouse2.jpg',
    }
  ];

  return (
    <div className="page-content">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img src="/container-yard.jpg" alt="Project Pipeline" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-dark/70 via-navy-primary/60 to-blue-accent/30"></div>

        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-accent/20 rounded-full blur-3xl animate-pulse"></div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-8 animate-fade-in-up">
            <span className="inline-block px-4 py-2 mb-6 text-sm font-medium tracking-widest uppercase bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              {t.pipeline.heroTag}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-4 tracking-tight">
              {t.pipeline.heroTitle}
            </h1>
            <p className="text-xl font-light opacity-90 max-w-2xl mx-auto">
              {t.pipeline.heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Projects Grid - Hover Cards */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {projects.map((project, idx) => (
              <div
                key={idx}
                className="group relative aspect-[4/3] overflow-hidden shadow-lg cursor-pointer"
              >
                {/* Background Image */}
                <img
                  src={project.image}
                  alt={project.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                {/* Default Overlay - shows location */}
                <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/80 via-navy-dark/20 to-transparent transition-opacity duration-500 group-hover:opacity-0">
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-xl font-medium text-white mb-1">{project.name}</h3>
                    <div className="flex items-center gap-2 text-white/80 text-sm">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>{project.location}</span>
                    </div>
                  </div>
                </div>

                {/* Hover Overlay - shows full details */}
                <div className="absolute inset-0 bg-navy-primary/95 opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col justify-center p-6">
                  <h3 className="text-xl font-medium text-white mb-3">{project.name}</h3>
                  <div className="flex items-center gap-2 text-blue-accent text-sm mb-4">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>{project.location}</span>
                  </div>

                  <div className="space-y-2 text-white/90 text-sm">
                    <p>{project.gfa}</p>
                    <p>{project.target}</p>
                    <p className="text-gold-accent font-medium pt-2">{project.investment}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-navy-primary mb-4">{t.pipeline.ctaTitle}</h2>
          <p className="text-gray-600 text-lg mb-8">
            {t.pipeline.ctaSubtitle}
          </p>
          <a
            href="/#contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-navy-primary to-navy-light text-white font-medium hover:shadow-2xl hover:shadow-navy-primary/20 transition-all duration-300 hover:-translate-y-1"
          >
            {t.home.getInTouch}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </section>
    </div>
  );
}

export default Pipeline;
